import { create } from 'zustand';
import {
  listCobroNotes,
  getCobroNote,
  voidCobroNote,
  notifyCobroNote,
  regenerateCobroPdf,
  markCobroNotesPaid,
  previewCobro,
  generateCobroNotes,
  registerPayment as registerPaymentApi,
  voidPayment as voidPaymentApi,
  allocatePayment as allocatePaymentApi,
  listCredits as listCreditsApi,
  listMyCobroNotes,
  getMyCobroNote,
  getPaymentMethods,
  createCharge,
  verifyPayment as verifyPaymentApi,
  getPaymentConfig,
  savePaymentConfig,
  getBankSettings,
  saveBankSettings,
  uploadPaymentProof,
  createTransfer,
  createCash,
  getTransferProof,
  confirmTransfer,
  rejectTransfer,
} from '../api/cobro';
import { getCollectionsDashboard } from '../api/collections';
import i18n from "../i18n/index.js";

const msg = (err) => err.response?.data?.message ?? err.message ?? i18n.t('Error');

// Tope de ids por request de marcado masivo. Coincide con el @ArrayMaxSize del DTO del
// backend; el troceo lo hace `markNotesPaid`.
const MARK_PAID_CHUNK = 100;

// Tope de notas impagas que el comercio ve en /home/pagos. Coincide con el tope del backend
// para `debt=true`: las impagas se piden aparte del historial para que una deuda vieja no
// quede fuera de la primera página (que va por fecha de creación, pagadas incluidas).
const MY_DEBT_LIMIT = 200;

// Secuencia de las requests de lista y de dashboard: solo la ÚLTIMA escribe. Sin esto, al
// alternar filtros rápido (Vencidas → Todas) la respuesta más lenta pisaba a la vigente y la
// lista quedaba con el lote de otro filtro, sin ningún aviso.
let notesSeq = 0;
let dashboardSeq = 0;

// Filtros de servidor que ACHICAN el lote de la lista en vez de filtrarlo en el cliente:
// "Vencidas" y las tarjetas "Por vencer (7 días)" y "Comercios con más de 1 nota impaga" del
// resumen. Son excluyentes (billing responde 400 si llega más de uno), así que todo lo que
// los lee pasa por `narrowingOf`, que devuelve uno solo.
export const NARROWING_FILTERS = ['overdue', 'dueSoon', 'multipleUnpaid'];

/** El filtro que achica el lote activo en `filters`, o '' si no hay ninguno. */
export const narrowingOf = (filters) =>
  NARROWING_FILTERS.find((key) => !!filters?.[key]) ?? '';

/** Parche de `filters` que deja prendido solo `key` ('' = ninguno) entre esos filtros. */
export const narrowingPatch = (key) =>
  Object.fromEntries(NARROWING_FILTERS.map((k) => [k, k === key]));

const useCobroStore = create((set, get) => ({
  // ── Operador ──
  notes: [],
  notesCount: 0,
  // Filtros de servidor con los que se pidió `notes` ({ companyId, holdingId, chargeMode,
  // narrowing }, con `narrowing` = narrowingOf(filters)), o null antes de la primera carga.
  // Mientras carga otro filtro, `filters` ya cambió pero `notes` sigue siendo el lote
  // anterior: esto dice a qué filtro corresponde lo que se ve.
  notesScope: null,
  // Error de la ÚLTIMA carga de la lista (o null). Aparte de `error`, que comparten todas
  // las acciones del store: la lista solo debe avisar que no pudo cargar, no que falló otra
  // cosa (un marcado como pagadas ya se avisa con su propio toast).
  notesError: null,
  selectedNote: null,
  // El estado de pago y el rango de fechas se filtran en el front, así que traemos un lote
  // amplio. Los filtros que achican el lote (NARROWING_FILTERS) son la excepción: van al
  // server, porque el lote se corta en 500 por fecha de creación y afuera quedarían justo las
  // notas más viejas, que son las que esos filtros buscan.
  filters: {
    paymentStatus: '',
    companyId: '',
    // Notas consolidadas de UN holding. Excluyente con `companyId` en la UI: la nota del
    // holding no tiene comercio, así que combinados no devolverían nada.
    holdingId: '',
    chargeMode: '',
    overdue: false,
    dueSoon: false,
    multipleUnpaid: false,
    limit: 500,
    offset: 0,
  },
  loading: false,
  loadingDetail: false,
  saving: false,
  previewing: false,
  preview: null,
  error: null,

  // ── Comercio ──
  myNotes: [],
  myNotesCount: 0,
  loadingMine: false,
  // Impagas del comercio, pedidas aparte del historial (ver MY_DEBT_LIMIT).
  myDebtNotes: [],
  myDebtCount: 0,
  loadingMyDebt: false,
  myDebtError: null,

  // ── Dashboard de cobranza (todo el operador, sin los filtros de la lista) ──
  dashboard: null,
  // Operador dueño de `dashboard`. El store es global y sobrevive a un cambio de sesión en
  // el mismo navegador: sin esta etiqueta, el siguiente usuario vería por un instante los
  // morosos del operador anterior hasta que llegue su propio resumen.
  dashboardOwnerId: null,
  loadingDashboard: false,
  // { status, message } del último intento fallido. El status deja ocultar el bloque a un
  // rol sin permiso (403) en vez de mostrarle un error.
  dashboardError: null,

  // ── Config de pago ──
  paymentConfigs: [],

  // Cambiar un filtro siempre reinicia a la primera página.
  setFilters: (newFilters) =>
    set((s) => ({ filters: { ...s.filters, ...newFilters, offset: 0 } })),

  // Paginación: mueve el offset sin tocar el resto de filtros.
  setOffset: (offset) =>
    set((s) => ({ filters: { ...s.filters, offset } })),

  // Prende UN filtro que achica el lote (o ninguno, con '') y apaga los demás. Limpia el
  // estado de pago: ese se filtra en el cliente, y combinado con un lote achicado dejaría las
  // tarjetas de estado contando otra cosa que la lista.
  setNarrowing: (key) =>
    set((s) => ({
      filters: { ...s.filters, ...narrowingPatch(key), paymentStatus: '', offset: 0 },
    })),

  fetchNotes: async () => {
    const seq = ++notesSeq;
    set({ loading: true, error: null, notesError: null });
    try {
      const { filters } = get();
      // Comercio u holding, modo y el filtro que achica el lote van al server; estado y
      // fechas se filtran en el front.
      const params = { limit: filters.limit ?? 500, offset: 0 };
      if (filters.companyId) params.companyId = filters.companyId;
      if (filters.holdingId) params.holdingId = filters.holdingId;
      if (filters.chargeMode) params.chargeMode = filters.chargeMode;
      const narrowing = narrowingOf(filters);
      if (narrowing) params[narrowing] = 'true';
      const scope = {
        companyId: filters.companyId || '',
        holdingId: filters.holdingId || '',
        chargeMode: filters.chargeMode || '',
        narrowing,
      };
      const { data } = await listCobroNotes(params);
      if (seq !== notesSeq) return;
      set({ notes: data.items ?? [], notesCount: data.count ?? 0, notesScope: scope });
    } catch (err) {
      if (seq === notesSeq) {
        const message = msg(err);
        set({ error: message, notesError: message });
      }
    } finally {
      // Una request vieja no apaga el loading de la vigente.
      if (seq === notesSeq) set({ loading: false });
    }
  },

  fetchDashboard: async (ownerId) => {
    const seq = ++dashboardSeq;
    if (get().dashboardOwnerId !== ownerId) set({ dashboard: null, dashboardOwnerId: ownerId });
    set({ loadingDashboard: true, dashboardError: null });
    try {
      const { data } = await getCollectionsDashboard();
      // Solo la última: un resumen pedido antes de marcar notas como pagadas, que llegue
      // después, dejaría montos anteriores al pago (o de otro operador si cambió la sesión).
      if (seq === dashboardSeq) set({ dashboard: data ?? null });
    } catch (err) {
      // Se conserva el último resumen bueno: sigue siendo cierto "al {asOf}" que muestra.
      if (seq === dashboardSeq) {
        set({ dashboardError: { status: err.response?.status ?? null, message: msg(err) } });
      }
    } finally {
      if (seq === dashboardSeq) set({ loadingDashboard: false });
    }
  },

  fetchNote: async (id) => {
    set({ loadingDetail: true, error: null });
    try {
      const { data } = await getCobroNote(id);
      set({ selectedNote: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ loadingDetail: false });
    }
  },

  clearSelectedNote: () => set({ selectedNote: null }),

  voiding: false,
  voidNote: async (id, reason) => {
    set({ voiding: true, error: null });
    try {
      const { data } = await voidCobroNote(id, reason);
      // Refresca el detalle (queda Anulada) y la lista del operador.
      await get().fetchNote(id).catch(() => {});
      get().fetchNotes();
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ voiding: false });
    }
  },

  notifying: false,
  notifyNote: async (id) => {
    set({ notifying: true, error: null });
    try {
      const { data } = await notifyCobroNote(id);
      return data; // { sent, recipients, reason }
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ notifying: false });
    }
  },

  regenerating: false,
  regeneratePdf: async (id) => {
    set({ regenerating: true, error: null });
    try {
      const { data } = await regenerateCobroPdf(id);
      // Refresca la nota para tomar la nueva pdfUrl.
      await get().fetchNote(id).catch(() => {});
      return data; // { ok, pdfUrl, reason }
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ regenerating: false });
    }
  },

  runPreview: async (payload) => {
    set({ previewing: true, error: null });
    try {
      const { data } = await previewCobro(payload);
      set({ preview: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ previewing: false });
    }
  },

  clearPreview: () => set({ preview: null }),

  generate: async (payload) => {
    set({ saving: true, error: null });
    try {
      const { data } = await generateCobroNotes(payload);
      get().fetchNotes();
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  // ── Comercio ──
  fetchMyNotes: async () => {
    set({ loadingMine: true, error: null });
    try {
      const { data } = await listMyCobroNotes({});
      set({ myNotes: data.items ?? [], myNotesCount: data.count ?? 0 });
    } catch (err) {
      set({ error: msg(err) });
    } finally {
      set({ loadingMine: false });
    }
  },

  fetchMyDebtNotes: async () => {
    set({ loadingMyDebt: true, myDebtError: null });
    try {
      const { data } = await listMyCobroNotes({ debt: 'true', limit: MY_DEBT_LIMIT });
      set({ myDebtNotes: data.items ?? [], myDebtCount: data.count ?? 0 });
    } catch (err) {
      set({ myDebtNotes: [], myDebtCount: 0, myDebtError: msg(err) });
    } finally {
      set({ loadingMyDebt: false });
    }
  },

  getMyNote: async (id) => {
    const { data } = await getMyCobroNote(id);
    return data;
  },

  // Reconcilia el pago contra el PSP (no depende del webhook). Best-effort: si
  // falla, la vista cae al re-fetch de la nota. Devuelve { status, noteId }.
  verifyPayment: async (noteId) => {
    const { data } = await verifyPaymentApi(noteId);
    return data;
  },

  // Métodos de pago disponibles para una nota (pasarelas activas del operador).
  fetchPaymentMethods: async (noteId) => {
    const { data } = await getPaymentMethods(noteId);
    return data; // { noteId, methods: [{ type, provider, environment }] }
  },

  pay: async (noteId, email, provider) => {
    set({ saving: true, error: null });
    try {
      const { data } = await createCharge({ noteId, email, provider });
      return data; // { paymentUrl, paymentRecordId, provider }
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  // ── Config de pago ──
  fetchPaymentConfig: async () => {
    try {
      const { data } = await getPaymentConfig();
      set({ paymentConfigs: Array.isArray(data) ? data : [] });
    } catch {
      set({ paymentConfigs: [] });
    }
  },

  savePaymentConfig: async (cfg) => {
    set({ saving: true, error: null });
    try {
      const { data } = await savePaymentConfig(cfg);
      get().fetchPaymentConfig();
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  // ── Datos bancarios del operador (transferencia manual) ──
  bankSettings: null,
  savingBank: false,

  fetchBankSettings: async () => {
    try {
      const { data } = await getBankSettings();
      set({ bankSettings: data ?? null });
    } catch {
      set({ bankSettings: null });
    }
  },

  saveBankSettings: async (payload) => {
    set({ savingBank: true, error: null });
    try {
      const { data } = await saveBankSettings(payload);
      set({ bankSettings: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ savingBank: false });
    }
  },

  // ── Transferencia manual ──
  // Comercio: sube el comprobante y registra la transferencia (queda en revisión).
  submittingTransfer: false,
  // `amount` es el monto declarado por el comercio (parcial). Si falta, integrations
  // cobra el saldo vigente de la nota.
  submitTransfer: async ({ noteId, email, file, amount }) => {
    set({ submittingTransfer: true, error: null });
    try {
      const up = await uploadPaymentProof(file, noteId);
      const f = up?.data?.files?.[0];
      if (!f?.location) throw new Error(i18n.t('No se pudo subir el comprobante.'));
      const { data } = await createTransfer({
        noteId,
        email,
        proofUrl: f.location,
        proofKey: f.key,
        proofMime: file.type,
        ...(Number.isFinite(amount) ? { amount } : {}),
      });
      return data; // { status: 'in_review', noteId }
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ submittingTransfer: false });
    }
  },

  // Comercio: informa un pago en efectivo (sin comprobante).
  submitCash: async ({ noteId, email, amount }) => {
    set({ submittingTransfer: true, error: null });
    try {
      const { data } = await createCash({
        noteId,
        email,
        ...(Number.isFinite(amount) ? { amount } : {}),
      });
      return data; // { status: 'in_review', noteId }
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ submittingTransfer: false });
    }
  },

  // Comprobante adjunto como blob (descarga autenticada).
  fetchTransferProof: async (noteId) => {
    const { data } = await getTransferProof(noteId);
    return data;
  },

  // Operador: confirma / rechaza un pago manual en revisión. `amount` es el monto
  // real recibido que edita el operador (opcional: integrations usa el vigente).
  confirming: false,
  confirmPayment: async (noteId, amount) => {
    set({ confirming: true, error: null });
    try {
      const { data } = await confirmTransfer(noteId, amount);
      await get().fetchNote(noteId).catch(() => {});
      get().fetchNotes();
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ confirming: false });
    }
  },

  // ── Pagos parciales (operador) ──
  registeringPayment: false,
  registerPayment: async (id, payload) => {
    set({ registeringPayment: true, error: null });
    try {
      const { data } = await registerPaymentApi(id, payload);
      // Refresca el detalle (saldo, historial, estado) y la lista (saldo/totales).
      await get().fetchNote(id).catch(() => {});
      get().fetchNotes();
      return data; // { note, payment }
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ registeringPayment: false });
    }
  },

  voidingPayment: false,
  voidPayment: async (id, paymentId, reason) => {
    set({ voidingPayment: true, error: null });
    try {
      const { data } = await voidPaymentApi(id, paymentId, reason);
      await get().fetchNote(id).catch(() => {});
      get().fetchNotes();
      return data; // { note }
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ voidingPayment: false });
    }
  },

  // Un abono repartido entre varias notas. La lista se recarga porque cambian los
  // saldos de todas las notas tocadas (y el dashboard lo refresca el llamador).
  allocating: false,
  allocatePayment: async (payload) => {
    set({ allocating: true, error: null });
    try {
      const { data } = await allocatePaymentApi(payload);
      get().fetchNotes();
      return data; // { allocationId, applied, credit }
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ allocating: false });
    }
  },

  // Saldos a favor abiertos del deudor ({ companyId } o { holdingId }).
  listCredits: async (params) => {
    const { data } = await listCreditsApi(params);
    return data; // { items, totalRemaining }
  },

  // Marcado masivo como pagadas. Se trocea porque el endpoint acepta hasta 200 ids por
  // request y el proxy del BFF corta a los 30 s; los resultados se acumulan para poder
  // decirle al operador exactamente cuáles no se marcaron y por qué.
  markingPaid: false,
  markNotesPaid: async (noteIds) => {
    set({ markingPaid: true, error: null });
    // Fuera del try: si un lote falla DESPUÉS de otro que ya marcó notas, lo acumulado no se
    // pierde y el llamador puede avisar qué quedó pagado y qué no.
    const acc = { updated: 0, ok: [], skipped: [] };
    try {
      const chunks = [];
      for (let i = 0; i < noteIds.length; i += MARK_PAID_CHUNK) {
        chunks.push(noteIds.slice(i, i + MARK_PAID_CHUNK));
      }
      for (const chunk of chunks) {
        const { data } = await markCobroNotesPaid(chunk);
        acc.updated += data?.updated ?? 0;
        acc.ok.push(...(data?.ok ?? []));
        acc.skipped.push(...(data?.skipped ?? []));
      }
      get().fetchNotes();
      return acc;
    } catch (err) {
      // Éxito parcial: los lotes anteriores SÍ marcaron notas, así que la lista se recarga
      // (si no, seguirían viéndose como impagas) y lo marcado viaja dentro del error.
      if (acc.updated > 0 || acc.ok.length || acc.skipped.length) {
        if (err && typeof err === 'object') err.partial = acc;
        get().fetchNotes();
      }
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ markingPaid: false });
    }
  },

  rejecting: false,
  rejectPayment: async (noteId, reason) => {
    set({ rejecting: true, error: null });
    try {
      const { data } = await rejectTransfer(noteId, reason);
      await get().fetchNote(noteId).catch(() => {});
      get().fetchNotes();
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ rejecting: false });
    }
  },
}));

export default useCobroStore;
